import {
	createContext,
	useContext,
	useMemo,
	FC,
	ReactNode,
	useState,
	useCallback,
	useEffect,
} from 'react';
import {
	Application,
	Feedback,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { useToastMessage } from '../../../hooks/useToastMessage';
import { useApplicationId } from '../../../hooks/useApplicationId';
import { INTERVIEW_LANDING } from '../../../shared/Routes';
import { useInterviewWizard } from '../useInterviewWizard';
import { transformToAnswersForMandatoryQuestionsFromGRPC } from '../../../shared/Utils';

export interface ViewCareTeamApplicantInterviewAnswersContextType {
	application: Partial<Application>;
	dietaryHabitsAndHealth: Feedback;
	setDietaryHabitsAndHealth: (feedback: Feedback) => void;
	onSubmitClick: () => void;
	onCancelClick: () => void;
}

interface Props {
	children: ReactNode;
}

const ViewCareTeamApplicantInterviewAnswersContext =
	createContext<ViewCareTeamApplicantInterviewAnswersContextType>({
		application: {},
		dietaryHabitsAndHealth: {},
		setDietaryHabitsAndHealth: () => {},
		onSubmitClick: () => {},
		onCancelClick: () => {},
	});

export const ViewCareTeamApplicantInterviewAnswersProvider: FC<Props> = ({
	children,
}: Props) => {
	const { showToastMessage } = useToastMessage();
	const [application, setApplication] = useState<Partial<Application>>({});
	const [dietaryHabitsAndHealth, setDietaryHabitsAndHealth] =
		useState<Feedback>({});
	const {
		applicationId,
		navigateWithApplicationId,
		fetchApplication: callFetchApplication,
	} = useApplicationId();
	const { onCancelClick } = useInterviewWizard();

	const onSubmitClick = useCallback(() => {
		navigateWithApplicationId(INTERVIEW_LANDING);
	}, [navigateWithApplicationId]);

	const value =
		useMemo<ViewCareTeamApplicantInterviewAnswersContextType>(() => {
			return {
				application,
				dietaryHabitsAndHealth,
				setDietaryHabitsAndHealth,
				onSubmitClick,
				onCancelClick,
			};
		}, [
			application,
			dietaryHabitsAndHealth,
			setDietaryHabitsAndHealth,
			onSubmitClick,
			onCancelClick,
		]);

	const fetchApplication = useCallback(async () => {
		if (!applicationId) {
			showToastMessage('error', 'applicationId is missing');
		}

		try {
			const fetchedApplication = await callFetchApplication();

			const applicantInterviewFeedbackWithAnswersForMandatoryQuestions =
				transformToAnswersForMandatoryQuestionsFromGRPC(
					fetchedApplication.applicantInterviewFeedback
				);

			if (
				!dietaryHabitsAndHealth ||
				Object.keys(dietaryHabitsAndHealth).length === 0
			) {
				setDietaryHabitsAndHealth(
					applicantInterviewFeedbackWithAnswersForMandatoryQuestions?.dietaryHabitsAndHealth ??
						{}
				);
			}
			setApplication(fetchedApplication);
		} catch (e) {
			// Handled in callApi
		}
	}, [
		applicationId,
		callFetchApplication,
		dietaryHabitsAndHealth,
		showToastMessage,
	]);

	useEffect(() => {
		void fetchApplication();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<ViewCareTeamApplicantInterviewAnswersContext.Provider value={value}>
			{children}
		</ViewCareTeamApplicantInterviewAnswersContext.Provider>
	);
};

export const useViewCareTeamApplicantInterviewAnswers = () => {
	return useContext(ViewCareTeamApplicantInterviewAnswersContext);
};
